import { useEffect, useRef, useState } from "react";
import { openConversationStream } from "../services/conversationLiveStream";

// Quanto tempo se espera antes de pedir a transcricao outra vez, juntando o que
// chegar pelo meio. Um paragrafo do agente publica message_start, text_chunk e
// message_complete em rajada (ver StreamingUtils._deliver_text no backend, que
// faz os tres em sequencia), portanto sem isto sao tres pedidos por paragrafo
// para o mesmo resultado.
//
// 250ms e um ponto de partida com uma razao e nao um numero medido: esta abaixo
// do que se nota e acima do intervalo entre aquelas tres chamadas. A tarefa 6
// olha para a aba de rede e confirma que um paragrafo da UM pedido.
const COALESCE_MS = 250;

/**
 * Liga uma conversa ao seu fio ao vivo.
 *
 * O hook nao tem a transcricao e nunca a toca: chama o `onChange` de quem o usa
 * e e esse que volta a pedi-la. Assim a transcricao continua a ter um dono so, o
 * que e o que impede duas versoes dela de existirem ao mesmo tempo.
 *
 * @param {string|undefined} conversationId
 * @param {{ onChange: () => Promise<boolean>, conversationStatus: string|undefined }} handlers
 * @returns {{ status: "connecting" | "live" | "offline" | "ended", typing: boolean }}
 */
export const useConversationLive = (conversationId, { onChange, conversationStatus }) => {
  const [status, setStatus] = useState("connecting");
  const [typing, setTyping] = useState(false);

  // So se abre o fio enquanto a conversa pode estar viva, e o `undefined` conta
  // como "ainda nao sabemos" e nao como "nao esta aberta": a primeira leitura
  // ainda nao respondeu, e trata-lo como fechado nunca abria o fio.
  //
  // Isto e a segunda porta para o mesmo defeito que o ramo do status que o
  // plano do backend especifica, e que ainda nao esta implantado, fecha pela
  // primeira, e e preciso porque a primeira depende de uma frame que pode nao
  // chegar. Se o `resolved` se perder, e o `publish` do backend engole as
  // proprias falhas com um `sse_publish_failed`, o fio fica aberto num canal
  // que e por CONTACTO: as frames da conversa seguinte do mesmo contacto chegam
  // aqui, e um `typing` delas desenha-se debaixo de uma transcricao acabada.
  // Aqui a conta fecha-se com dados que o componente ja tem, porque a resposta
  // do detalhe traz o `status`.
  //
  // Limite honesto, e e do RPAA-231: se o `resolved` se perder E nao vier mais
  // nenhuma frame, nada provoca uma leitura e nada aprende. O mecanismo e o
  // ConversationDetail, que nao arma o setInterval com o fio vivo, portanto sem
  // sondagem este portao nunca chega a ver um `status` novo. A unica excecao e
  // uma mensagem do cliente a espera da ponte de handoff (o `awaitingBridge`),
  // que sonda ate deixar de haver uma.
  //
  // O fecho automatico por inactividade do RPAA-231 e exactamente esse caso, e
  // quem o construir tem de publicar um `resolved` ou aceitar que um fio aberto
  // sobrevive ao fecho. Era a fase 6.5 do RPAA-201 ate 2026-09-08, cancelada por
  // o RPAA-231 ser um superconjunto dela; a medicao esta comentada no bilhete.
  const mayBeLive = conversationStatus === undefined || conversationStatus === "open";

  // O `onChange` de quem chama e recriado a cada render (e um useCallback que
  // depende do estado dele), e o efeito abaixo NAO pode depender dele: uma
  // dependencia que muda a cada render fecharia e reabriria o fio a cada render,
  // que e um fio novo por tecla premida em qualquer parte da pagina. A ref e o
  // que deixa o efeito depender so do id da conversa.
  const changeRef = useRef(onChange);
  useEffect(() => {
    changeRef.current = onChange;
  }, [onChange]);

  // Uma conversa que se soube fechada acabou, e a etiqueta tem de o dizer mesmo
  // quando foi esta leitura a descobri-lo e nao uma frame.
  //
  // O `setTyping(false)` aqui nao e simetria, e o que impede um indicador preso,
  // e a sequencia e a da observacao 8 do browser. O visitante escreve, o eco da
  // mensagem dele e um `typing: true` sao publicados a milissegundos um do
  // outro, o eco aglutina 250ms e so entao a leitura revela `resolved`. Nessa
  // altura o `typing` ja esta a true, este efeito fecha o fio, e nenhum
  // `typing: false` pode voltar a chegar: "o agente esta a escrever" ficava
  // debaixo de uma transcricao acabada ate o componente remontar, que e
  // exactamente o que a observacao 8 diz que nao pode acontecer. Os outros tres
  // `setTyping(false)` estao no `onStatus`, no `live` e no `resolved`, e nenhum
  // deles corre neste caminho.
  useEffect(() => {
    if (!mayBeLive) {
      setStatus("ended");
      setTyping(false);
    }
  }, [mayBeLive]);

  useEffect(() => {
    if (!conversationId || !mayBeLive) return undefined;

    let coalesceTimer = null;
    let stream = null;
    // `ended` e terminal e nada o desfaz. Guardado aqui e nao no estado porque e
    // lido dentro dos callbacks do fio, onde o valor do estado esta congelado no
    // render em que o efeito correu.
    let ended = false;

    const askForTranscript = async () => {
      const ok = await changeRef.current();
      // Nao basta trocar a etiqueta para "offline". Sem reabrir, o fio continua
      // vivo e a entregar transcript_changed enquanto a sondagem tambem corre,
      // que sao os dois transportes ao mesmo tempo, e nada volta a por o estado
      // em `live` porque isso so acontece num `live` novo. Reabrir devolve a
      // maquina de estados ao caminho normal: offline, backoff, live, pedido.
      //
      // So enquanto a conversa nao acabou: depois do `resolved` o fio esta
      // fechado de propria vontade e reabrir seria voltar a subscrever uma
      // conversa que o ramo do status que o plano do backend especifica, e que
      // ainda nao esta implantado, recusaria. O guarda `!ended` nao depende
      // desse ramo: evita reabrir um fio que este codigo ja fechou por conta
      // propria.
      if (!ok && !ended) {
        setStatus("offline");
        stream.reconnect();
      }
    };

    const askSoon = () => {
      if (coalesceTimer) clearTimeout(coalesceTimer);
      coalesceTimer = setTimeout(() => {
        coalesceTimer = null;
        void askForTranscript();
      }, COALESCE_MS);
    };

    stream = openConversationStream({
      conversationId,
      onStatus: (next) => {
        if (ended) return;
        setStatus(next);
        // Um fio novo nao sabe se o agente estava a escrever, e a frame de OFF
        // pode ter caido no intervalo. Sem isto, "o agente esta a escrever"
        // ficava preso ate um OFF de um turno mais tarde, que e a falha que o
        // notification_for nomeia, no plano do backend ainda nao implantado,
        // para as frames malformadas e que uma queda de fio produz do mesmo
        // modo.
        setTyping(false);
      },
      onEvent: (event) => {
        if (ended) return;
        if (event.type === "live") {
          setStatus("live");
          setTyping(false);
          // Sem aglutinar: e este pedido a entrada tardia, e atrasa-lo 250ms era
          // atrasar a primeira coisa que o operador espera ver.
          void askForTranscript();
          return;
        }
        if (event.type === "typing") {
          setTyping(Boolean(event.on));
          return;
        }
        if (event.type === "resolved") {
          // Terminal, e pode ser o PRIMEIRO evento: uma conversa que ja nao
          // estava aberta no connect recebe isto e nunca um `live`, segundo o
          // ramo do status que o plano do backend especifica, e que ainda nao
          // esta implantado. O codigo abaixo nao depende desse ramo: trata um
          // `resolved` como primeiro evento como um evento normal, chegue ele
          // por essa via ou por outra.
          ended = true;
          setTyping(false);
          setStatus("ended");
          // Um ultimo pedido, tambem sem aglutinar. Nao e redundante com o
          // `load` da montagem: um turno em voo pode escrever nesta conversa
          // depois de ela ser resolvida, porque nada predica uma escrita de
          // mensagem no status da CONVERSA (o append_text e o set_status olham
          // so para messages.status).
          void askForTranscript();
          if (coalesceTimer) clearTimeout(coalesceTimer);
          stream.close();
          return;
        }
        // transcript_changed, e qualquer tipo que este codigo nao conheca.
        askSoon();
      },
    });

    return () => {
      if (coalesceTimer) clearTimeout(coalesceTimer);
      stream.close();
    };
    // `mayBeLive` entra aqui de proposito: quando passa a falso, o cleanup
    // fecha o fio, o corpo volta a correr e sai logo no guarda. E esse o
    // mecanismo que fecha o fio de uma conversa que a leitura revelou acabada.
  }, [conversationId, mayBeLive]);

  // Devolvido a parte do estado, e nao dentro dele: quem consome liga o `typing`
  // a um sinal no ecra e o `status` a decisao de sondar, e sao duas coisas diferentes.
  return { status, typing };
};

export default useConversationLive;
